/* eslint-disable react/display-name */
import React, { forwardRef, ReactNode, Ref } from 'react';
import { Input as AntInput, InputProps, InputRef } from 'antd';
import './Input.scss';
import { twMerge } from '@shared/utils/twMerge';

export type CustomInputProps = InputProps & {
  dark?: boolean;
  label?: string;
  errors?: string;
  inOneLine?: boolean;
  asText?: boolean;
  classNameInputWrapper?: string;
  classNameContainer?: string;
  classNameLabel?: string;
  classNameLabelText?: string;
  classNameError?: string;
  extraHelper?: ReactNode;
  disabledOpacity?: number;
  disabled?: boolean;
};

const inputWrapperDefault = 'max-w-[41.2rem] grow';

export const Input = forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      asText,
      dark,
      inOneLine,
      required,
      classNameLabel,
      classNameInputWrapper,
      classNameContainer='w-full',
      extraHelper,
      classNameLabelText,
      disabledOpacity = 30,
      disabled,
      ...props
    }: CustomInputProps,
    ref
  ) => {
    const styleInOneLineWrapper = inOneLine ? 'flex-row justify-between' : '';
    const styleInOneLineLabel = inOneLine ? 'pt-[1.2rem]' : '';
    return (
      <div
        className={classNameContainer}
        data-testid={`${props.name ?? 'user'}-input-container`}
      >
        <label
          className={twMerge(
            'flex w-full flex-col',
            styleInOneLineWrapper,
            disabled ? `opacity-${disabledOpacity}` : undefined,
            classNameLabel
          )}
        >
          <div className={twMerge('flex items-center', styleInOneLineLabel)}>
            {props.label && (
              <span
                className={twMerge(
                  'text-1424 font-semibold',
                  required && !asText && 'field-required',
                  classNameLabelText
                )}
              >
                {props.label}
              </span>
            )}
            {extraHelper}
          </div>
          <div className={twMerge(inputWrapperDefault, classNameInputWrapper)}>
            <AntInput
              {...props}
              readOnly={asText || props.readOnly}
              tabIndex={asText ? -1 : 0}
              ref={ref as unknown as Ref<InputRef>}
              className={twMerge(
                'D7-Input',
                dark && 'dark',
                props.errors && 'ant-input-error',
                props.className,
                asText && 'input-as-text'
              )}
              disabled={disabled}
            />
            <span
              data-testid={`${props.name}-error-container`}
              className={twMerge(
                'ant-input-error text-1420 font-normal',
                props.errors ? 'visible' : 'invisible',
                props.classNameError
              )}
            >
              {props.errors}
            </span>
          </div>
        </label>
      </div>
    );
  }
);
